import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Icons } from '@/components/icons';
import { locale } from '@/constants/locale';
import { APP_TOKEN } from '@/constants/common';
import { bffSecretHeaders } from '@/lib/client-ip';
import { logger } from '@/lib/logger';

const {
  auth: { responseModal, icons },
  layout: { breadcrumb: breadcrumbLocale }
} = locale;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function safeParseInt(val: string | null, fallback = 0): number {
  const parsed = parseInt(val ?? `${fallback}`, 10);
  return isNaN(parsed) ? fallback : parsed;
}

const loadSwal = () => import('sweetalert2').then((mod) => mod.default);

const logSwalError = (error: unknown) =>
  logger.error('[modal] failed to open sweetalert2 dialog:', error);

export const showErrorModal = ({
  title,
  text
}: {
  title?: string;
  text: string | undefined;
}) => {
  loadSwal()
    .then((Swal) =>
      Swal.fire({
        title: title || responseModal.somethingWentWrong,
        text: text?.toString(),
        icon: 'error',
        confirmButtonColor: '#2fd1cd'
        // showConfirmButton: false,
        // timer: 2000
      })
    )
    .catch(logSwalError);
};

export const showSuccessModal = ({
  title,
  text,
  secondaryAction
}: {
  title?: string;
  text: string | undefined;
  secondaryAction?: { label: string; onClick: () => void };
}) => {
  loadSwal()
    .then((Swal) =>
      Swal.fire({
        title: title || responseModal.success,
        text: text?.toString(),
        icon: 'success',
        confirmButtonColor: '#2fd1cd',
        showCancelButton: !!secondaryAction,
        cancelButtonText: secondaryAction?.label,
        cancelButtonColor: '#9ca3af'
      }).then((result) => {
        if (result.dismiss === Swal.DismissReason.cancel) {
          secondaryAction?.onClick();
        }
      })
    )
    .catch(logSwalError);
};

export const getInitials = (fullName: string): string => {
  if (!fullName) return '';
  const nameParts = fullName.trim().split(/\s+/);

  const initials = nameParts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  return initials || '';
};

// current date
export const currentYear = new Date().getFullYear();

export const capitalizeFirstLetter = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const convertToRoutes = (
  path: string
): { title: string; link: string }[] => {
  const segments = path.split('/').filter((segment) => segment);
  return segments.map((segment) => ({
    title: Object.keys(breadcrumbLocale).includes(
      segment as keyof typeof breadcrumbLocale
    )
      ? breadcrumbLocale?.[segment as keyof typeof breadcrumbLocale]
      : capitalizeFirstLetter(segment.replace(/-/g, ' ')),
    link: `/${segment}` // Keep the route format
  }));
};

type TagType = 'Diamond' | 'Flame' | 'BadgePercent' | 'Leaf' | 'Other';
type IconKey = keyof typeof Icons;
const tagData: Record<
  TagType,
  { label: string; color: string; icon: IconKey }
> = {
  Diamond: { label: icons.exclusive, color: '#01095C', icon: 'diamond' },
  Flame: { label: icons.hot, color: '#BF0016', icon: 'fire' },
  BadgePercent: { label: icons.sale, color: '#B84A00', icon: 'badgePercent' },
  Leaf: { label: icons.sustainable, color: '#015C0D', icon: 'leaf' },
  Other: { label: icons.other, color: '#7A5F00', icon: 'fire' }
};

const iconNames = Object.keys(tagData);

export const getBadges = (tags: { iconName: string }[] = []) => {
  return (
    tags?.map((tag) =>
      iconNames?.includes(tag?.iconName)
        ? tagData[tag.iconName as TagType]
        : tagData.Other
    ) || []
  );
};

export const sitemapFetch = async (
  url: string,
  options: RequestInit = {
    headers: { 'app-token': APP_TOKEN, ...bffSecretHeaders() }
  }
) => {
  try {
    const res = await fetch(url, {
      signal: AbortSignal.timeout(5000),
      ...options
    });
    if (!res.ok) {
      logger.error(`Failed to fetch ${url}: ${res.statusText}`);
      return { data: [] }; // Return a default structure
    }
    return res.json();
  } catch (error) {
    logger.error(`Error fetching ${url}:`, error);
    return { data: [] }; // Return a default structure
  }
};

export function truncateFileName(
  fileName: string,
  maxLength: number = 20
): string {
  if (fileName.length <= maxLength) return fileName;

  const dotIndex = fileName.lastIndexOf('.');
  const hasExtension = dotIndex > 0 && dotIndex < fileName.length - 1;
  const extension = hasExtension ? fileName.slice(dotIndex) : '';
  const name = hasExtension ? fileName.slice(0, dotIndex) : fileName;
  const ellipsis = '...';

  const availableLength = maxLength - extension.length - ellipsis.length;
  if (availableLength <= 0)
    return fileName.slice(0, maxLength - ellipsis.length) + ellipsis;

  const front = name.slice(0, Math.ceil(availableLength / 2));
  const back = name.slice(-Math.floor(availableLength / 2));

  return `${front}${ellipsis}${back}${extension}`;
}

export function getCountdownDays(
  validUntil: string | undefined
): number | null {
  if (!validUntil) return null;
  const now = new Date();
  const end = new Date(validUntil);
  const diffMs = end.getTime() - now.getTime();
  if (diffMs <= 0) return null; // expired — never show
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  if (diffDays >= 8) return null; // only show within 8-day threshold
  return Math.ceil(diffDays);
}

interface DebouncedFn {
  (): void;
  cancel: () => void;
}

export function debounce(func: () => void, wait: number): DebouncedFn {
  let timeout: ReturnType<typeof setTimeout>;
  const debounced = () => {
    clearTimeout(timeout);
    timeout = setTimeout(func, wait);
  };
  debounced.cancel = () => {
    clearTimeout(timeout);
  };
  return debounced;
}

// Whether a rich-text value has any visible content. The editor saves things
// like "<p></p>" or "<p><br></p>" for a blank field; those are truthy strings
// but render nothing, so a naive `value && value.trim()` check is misleading.
export const hasRichTextContent = (html?: string | null): boolean => {
  if (!html) return false;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;|\s/g, '') !== '';
};

// Strip HTML tags and convert list items to comma-separated text
export const stripHtmlToText = (html: string): string => {
  // Replace <li>...</li> with comma-separated items
  const listText = html
    .replace(/<\/?span[^>]*>/g, '') // remove span tags
    .replace(/<\/?p[^>]*>/g, '') // remove p tags
    .replace(/<\/?ul[^>]*>/g, '') // remove ul tags
    .replace(/<li[^>]*>/g, '') // remove opening li
    .replace(/<\/li>/g, ', ') // replace closing li with comma
    .trim();

  // Remove any remaining HTML tags
  const clean = listText.replace(/<\/?[^>]+(>|$)/g, '').trim();

  // Remove trailing comma
  return clean.replace(/,\s*$/, '');
};
